/**
 * Active workspace store — Feature 075 (plan 013, Workstream B).
 *
 * Tiny framework-free store holding the `workspaceOwnerId` a SUB_DELEGATE
 * is currently acting in. Lives outside React so both the axios client
 * (`x-workspace-owner-id` header) and the socket service (handshake auth
 * payload) can read it without a hook. Synced to localStorage so the
 * selection survives reloads.
 *
 * For non-delegate users the store is simply never set — every consumer
 * treats `null` as "no delegation, behave exactly as today".
 */

const STORAGE_KEY = 'bquest.activeWorkspaceOwnerId';

type Listener = () => void;

const readPersisted = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

let activeWorkspaceOwnerId: string | null =
  typeof window === 'undefined' ? null : readPersisted();

const listeners = new Set<Listener>();

const notify = () => listeners.forEach((listener) => listener());

export const activeWorkspaceStore = {
  get(): string | null {
    return activeWorkspaceOwnerId;
  },

  set(ownerId: string | null): void {
    if (ownerId === activeWorkspaceOwnerId) return;
    activeWorkspaceOwnerId = ownerId;
    try {
      if (ownerId === null) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, ownerId);
      }
    } catch {
      // localStorage unavailable (private mode) — in-memory value still works.
    }
    notify();
  },

  clear(): void {
    this.set(null);
  },

  /** Returns an unsubscribe function. Compatible with useSyncExternalStore. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
